import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { faSave, faUndo, faLock } from '@fortawesome/free-solid-svg-icons';
import { regionsMap } from 'projects/marketpro-ui/src/app/regions';
import { BehaviorSubject, Subject, of, timer } from 'rxjs';
import {
  debounce,
  debounceTime,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';
import { AgeGroup } from '../../models/age-group.model';
import { Currency } from '../../models/currency.enum';
import { DashboardFilter } from '../../models/custom-dashboard.model';
import { DataLens } from '../../models/data-lens.enum';
import { Gender } from '../../models/gender.enum';
import {
  NameValuePair,
  ToggleableNameValuePair,
} from '../../models/name-value-pair.model';
import { RangeFilterType } from '../../models/range-filter-type.enum';
import { Region } from '../../models/region-model';
import { RegionSelection } from '../../models/region-selection.model';
import { SelectorCountry } from '../../models/selector-country.model';
import { SelectorProvince } from '../../models/selector-province.model';
import { SpendingGroup } from '../../models/spending-group.model';
import { DashboardStatisticsService } from '../../services/dashboard-statistics.service';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { DataService } from '../../services/data.service';
import { SystemGroupsService } from '../../services/system-groups.service';
import { UpsaleModalService } from '../../services/upsale-modal.service';
import { CategoryService } from '../../services/categories.service';
import { countries, countriesMap } from '../../../../countries';
import { subnational } from '../../../../subnational';
import { Category } from '../../models/category.model';
import {
  ColorNames,
  CustomStep,
  DropdownComponent,
  Utils,
} from 'marketpro-design-lib';
import { IncomeGroup } from '../../models/income-group.model';
import { CustomStepsService } from '../../services/year-custom-steps.service';

@Component({
  selector: 'mpro-freemium-header',
  templateUrl: './freemium-header.component.html',
  styleUrls: ['./freemium-header.component.scss'],
})
export class FreemiumHeaderComponent implements OnInit, OnDestroy {
  @ViewChildren('dropdowns') dropdownChlidrens: QueryList<DropdownComponent>;
  public getNameValuePair = DataFilters.getNameValuePair;
  public loadingCategories = false;
  private currentCategoryDropdown: DropdownComponent;

  public faSave = faSave;
  public faUndo = faUndo;
  public faLock = faLock;
  public labelGap = 1;
  public currencyView;
  public spendingPer;
  public customIncomeGroups: IncomeGroup[] = [];
  public customUserGroups: {
    type: string;
    dropdownOpen: boolean;
    group: SpendingGroup[] | IncomeGroup[];
  }[];
  public customAgeGroups: AgeGroup[] = [];
  public customSpendingGroups: SpendingGroup[] = [];
  public dataLensOptions = constants.data.dataLensOptions.slice(0);
  public dataFilters: DataFilters = new DataFilters();
  public yearSliderError?: string;
  public countries: Map<string, SelectorCountry> = new Map();
  public startYear: number;
  public endYear: number;
  public disabledYears: number[] = [];
  public generationsOptions = constants.generationList.slice(0);

  // hacky - should be handled with alert service for all alerts
  @Output() public yearSliderErrorEmitter: EventEmitter<string | undefined> =
    new EventEmitter();
  public dashboardFilters: DashboardFilter[] = [];
  public dataLensInputValue?: NameValuePair;
  public categoryInputValue?: NameValuePair;

  private regionSelectionChanges: Subject<{
    selection: RegionSelection;
    dataLens?: number;
  }> = new Subject();
  private ngUnsubscribe: Subject<void> = new Subject();
  public categoriesList;
  public customSteps: BehaviorSubject<CustomStep[]>;

  private isRootCategory = false;
  constructor(
    public categoryService: CategoryService,
    private dataService: DataService,
    private upsaleModalService: UpsaleModalService,
    public authService: AuthService,
    public systemGroupsService: SystemGroupsService,
    public authResources: AuthorizedResourcesService,
    public dataFiltersService: DataFiltersService,
    public dashboardService: DashboardStatisticsService,
    public customStepsService: CustomStepsService
  ) {}

  public ngOnInit(): void {
    this.customSteps = this.customStepsService.currentCustomSteps;
    this.startYear = this.authResources.startYear;
    this.endYear = this.authResources.endYear;
    this.categoriesList = this.categoryService.getList(true);
    for (let i = this.startYear + 2; i <= this.authResources.endYear; i++) {
      if (![2030, 2034].includes(i)) {
        this.disabledYears.push(i);
      }
    }
    this.currencyView = constants.data.currencyView.map((item) => ({
      ...item,
      hidden: item.name === '2021 PPP USD',
    }));
    this.spendingPer = constants.data.spendingPer.sort(
      (a, b) => a.value - b.value
    );
    this.authResources.countries.forEach((c) => this.countries.set(c.code, c));

    this.authService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.resetAll(); // set authorized headers
        // dataService init is required to initialize the dataService and subscribe to filter changes
        // after the HeaderComponent is initialized
        this.dataService.init({
          ...new DataFilters(),
          ...this.getAuthDataFilters(),
        });
        if (this.authService.isLoggedIn()) {
          this.loadRangeCustomFilters();
          this.dataLensInputValue = this.getNameValuePair(
            constants.data.dataLensOptions,
            this.dataFilters.dataLens
          );
        } else {
          this.dataLensOptions = this.dataLensOptions.map((dataLens) => {
            return {
              ...dataLens,
              disabled: false,
              authDisabled: true,
            };
          });
          this.dataLensOptions.unshift(
            new ToggleableNameValuePair('Continental', -1)
          );
          this.dataLensInputValue = this.dataLensOptions[0];
        }
      });
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((filters) => {
        this.customStepsService.getCustomSteps(true);
        this.dataFilters = filters;
      });

    // delay the emitting a bit to counteract fast region selection
    this.regionSelectionChanges
      .pipe(takeUntil(this.ngUnsubscribe), debounceTime(700))
      .subscribe((data) => {
        this.dataFiltersService.patchDataFilters({
          regionSelection: data.selection,
          ...(data.dataLens !== undefined && { dataLens: data.dataLens }),
        });
      });
    this.startYear = this.authResources.startYear;
    this.getCategories();

    this.dataFiltersService.patchDataFilters({
      spendingPer: 1,
    });
    this.sliderChanged([2025]);
  }

  public ngOnDestroy(): void {
    this.dataService.reset();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public toggleDropdowns(dropdowns: DropdownComponent[]) {
    dropdowns.forEach((dropdown, index) => {
      dropdown.dropdownOpen = !index;
    });
  }

  private getCategories() {
    this.categoryService.getCategories$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounce((request) =>
          !this.categoryService.categoriesChildren.has(request?.code || '')
            ? timer(300)
            : of()
        ),
        switchMap((request) => {
          if (request === undefined) {
            return of([]);
          }
          this.dropdownChlidrens.forEach((dropdownChild) => {
            if (this.isRootCategory) {
              if (dropdownChild !== this.currentCategoryDropdown) {
                dropdownChild.closeDropdown();
              } else {
                dropdownChild.toggleDropdown();
              }
              this.loadingCategories = true;
            }
          });
          if (request.isCustom) {
            this.categoryService.categoriesDropdown$.next(
              request.childrenCategories || []
            );
            this.loadingCategories = false;
            return of([]);
          }
          return this.categoryService
            .getCategories(request.code)
            .pipe(tap(() => (this.loadingCategories = false)));
        })
      )
      .subscribe(() => {});
  }

  public onErrorChanged(error?: string) {
    this.yearSliderErrorEmitter.emit(error);
    this.yearSliderError = error;
    if (!this.authService.isLoggedIn() && error) {
      this.upsaleModalService.open();
    }
  }

  public getCategoryName(c: Category) {
    if (c.isCustom) {
      return `Category - ${this.currentCategoryDropdown?.title} ${c.label}`;
    }
    return 'Category - ' + c.label;
  }

  public onCategoryHover(
    c: Category,
    dropdown: DropdownComponent,
    isRootCategory: boolean
  ) {
    this.isRootCategory = isRootCategory;
    if (!c.childrenSize) {
      return;
    }
    this.currentCategoryDropdown = dropdown;
    this.categoryService.getCategories$.next(c);
  }

  public isChildDropdownOpen(c: Category) {
    return c?.childrenCategories ? c?.childrenCategories?.length !== 0 : false;
  }

  public isThirdLevelCategory(c: Category) {
    if (!c.code) {
      return true;
    }
    return !(c.code.replace(/[^.]/g, '').length === 2);
  }

  public dataLensChanged(selection: number) {
    if (!this.authService.isLoggedIn()) {
      setTimeout(() => {
        this.dataLensInputValue = { ...this.dataLensOptions[0] };
      });
      this.upsaleModalService.open();
      return;
    }
    this.dataFiltersService.patchDataFilters({
      dataLens: selection,
    });
  }

  public getHoverColor() {
    return Utils.getColorHex({ name: ColorNames.Blue, shade: 300 });
  }

  public genderChanged(selection: NameValuePair) {
    this.authService.openFreemiumRequestModal.next({
      isOpen: true,
      requestCategory: 'genders',
    });
  }

  public ageGroupChanged(ageGroup: AgeGroup) {
    // TODO: add same value check
    this.dataFiltersService.patchDataFilters({ ageGroup });
  }

  public spendingGroupChanged(spendingGroup: SpendingGroup | IncomeGroup) {
    // TODO: add same value check
    this.dataFiltersService.patchDataFilters({ spendingGroup });
  }

  public sliderChanged(year: (number | undefined)[]) {
    if (
      this.yearSliderError ||
      year[0] === this.dataFiltersService.getDataFiltersValue().year
    ) {
      return;
    }

    this.dataFiltersService.patchDataFilters({ year: year[0] });
  }

  public loadRangeCustomFilters() {
    if (!this.authService.isLoggedIn()) {
      return;
    }

    this.dashboardService
      .loadRangeCustomFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rangeFilters) => {
        this.customAgeGroups = rangeFilters
          .filter((range) => range.type == RangeFilterType.AGE)
          .map((rangeFilter) => AgeGroup.from(rangeFilter));

        this.dashboardService.setCustomAgeGroups(this.customAgeGroups);

        this.customSpendingGroups = rangeFilters
          .filter((range) => range.type == RangeFilterType.SPENDING)
          .map((rangeFilter) => SpendingGroup.from(rangeFilter));
        this.customIncomeGroups = rangeFilters
          .filter((range) => range.type == RangeFilterType.INCOME)
          .map((rangeFilter) => SpendingGroup.from(rangeFilter));
        this.dashboardService.setCustomSpendingGroups(
          this.customSpendingGroups
        );
      });
  }

  public loadCustomDashboards() {
    this.dashboardService
      .loadCustomDashboards()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((dashboards) => (this.dashboardFilters = dashboards));
  }

  public resetAll() {
    const resetFilters = {
      ...new DataFilters(),
      ...this.getAuthDataFilters(),
    };

    // Setting
    resetFilters.spendingPer = 1;

    this.dataFiltersService.setDataFilters(resetFilters);
  }

  public onCountryAndRegionChange(componentState: {
    selection: RegionSelection;
    dataLens?: number;
  }) {
    this.regionSelectionChanges.next(componentState);
  }

  public onSpendingPerChange(spendingPer: number) {
    this.dataFiltersService.patchDataFilters({ spendingPer });
  }

  public onCurrencyViewChange(currency: number) {
    this.dataFiltersService.patchDataFilters({ currency });
  }

  public onFreemiumCategoryChange(
    freemiumCategory: Category,
    freemiumCategoryDropdown: DropdownComponent
  ) {
    this.dataFiltersService.patchDataFilters({ freemiumCategory });
    freemiumCategoryDropdown.closeDropdown();
  }

  public onCategoryChange(category: string) {
    this.dataFiltersService.patchDataFilters({ category });
  }

  public onDashboardClicked(dashboard: string) {
    const newFilter = this.dashboardFilters.find((d) => d.name == dashboard);
    if (!newFilter) {
      this.dataFiltersService.setDataFilters({
        ...new DataFilters(),
        ...this.getAuthDataFilters(),
        dashboard,
      });
      return;
    }

    let newDataFilters = {
      ...new DataFilters(),
      ...this.getAuthDataFilters(),
    };

    Object.assign(newDataFilters, newFilter);

    if (newFilter?.ageGroup) {
      let newAgeGroup = new AgeGroup();
      Object.assign(newAgeGroup, newFilter?.ageGroup);
      newDataFilters.ageGroup = newAgeGroup;
    }

    if (newFilter?.spendingGroup) {
      let newSpendingGroup = new SpendingGroup();
      Object.assign(newSpendingGroup, newFilter?.spendingGroup);
      newDataFilters.spendingGroup = newSpendingGroup;
    }

    if (newFilter?.regionSelection) {
      newDataFilters.regionSelection =
        this.createDashboardRegionSelection(newFilter);
    } else if (
      /* It can be undefined coming from backend*/ newFilter.continentCodes &&
      newFilter.continentCodes.length &&
      regionsMap.has(newFilter.continentCodes.values().next().value || '')
    ) {
      // We have single selection, so use single value only for now.
      // If we bring back multi selection, this will also need to be modified.
      newDataFilters.regionSelection = new RegionSelection();
      newDataFilters.regionSelection.toggleRegion(
        regionsMap.get(newFilter.continentCodes.values().next().value || '')!,
        this.countries
      );
    } else if (newFilter.world && newFilter.world.childStates) {
      const countryCode = countriesMap.get(
        Object.keys(newFilter.world.childStates)[0]
      );
      if (countryCode) {
        newDataFilters.regionSelection = new RegionSelection();
        newDataFilters.regionSelection.toggleCountry(countryCode);
      }
    }

    if (newFilter?.gender) {
      if (newFilter.gender == Gender.MALE) {
        newDataFilters.gender = 'male';
      } else if (newFilter.gender == Gender.FEMALE) {
        newDataFilters.gender = 'female';
      }
    }

    if (newFilter?.currency) {
      if (newFilter.currency == Currency.PPP_USD_2017) {
        newDataFilters.currency = 0;
      } else if (newFilter.currency == Currency.REAL) {
        newDataFilters.currency = 1;
      }
    }

    newDataFilters.spendingPer = 1;

    if (newFilter?.dataLens) {
      switch (newFilter.dataLens) {
        case DataLens.NATIONAL:
          newDataFilters.dataLens = 0;
          break;
        case DataLens.SUBNATIONAL_PROVINCES:
          newDataFilters.dataLens = 1;
          break;
        case DataLens.SUBNATIONAL_CITIES:
          newDataFilters.dataLens = 2;
          break;
        default:
          break;
      }
    }

    newDataFilters.dashboard = newFilter?.name ?? '';
    this.dataFiltersService.setDataFilters(newDataFilters);
  }

  public updateDataLensDisabledStatus(): void {
    if (
      this.dataFilters.regionSelection.countries.size === 1 &&
      [...this.dataFilters.regionSelection.countries.values()][0].subnational
        ?.length
    ) {
      this.dataLensOptions[1].disabled = false;
    } else {
      this.dataLensOptions[1].disabled = true;
    }
  }

  public handleLockedYearSlider(e) {
    return this.authService.openFreemiumRequestModal.next({
      isOpen: true,
      requestCategory: `${e.currentValue} data access`,
    });
  }

  private createDashboardRegionSelection(
    filter: DashboardFilter
  ): RegionSelection {
    const subnationalMap: Map<string, SelectorProvince> = new Map();
    const countriesMap: Map<string, SelectorCountry> = new Map();
    const regionMap: Map<string, Region> = new Map();

    const countriesCodes = filter.regionSelection?.countries;
    countriesCodes?.forEach((element) => {
      const selCountry = new SelectorCountry(
        element,
        countries.find((c) => c.code == element)?.name!
      );
      countriesMap.set(element, selCountry);
    });

    const subnationalsCodes = filter.regionSelection?.subnationals;
    subnationalsCodes?.forEach((s) => {
      for (let i = 0; i < countriesCodes!.length; i++) {
        const countryCode = countriesCodes![i];
        if (subnational[countryCode] && subnational[countryCode][s]) {
          const selSubnational = new SelectorProvince(
            s,
            subnational[countryCode][s]
          );
          subnationalMap.set(s, selSubnational);
        }
      }
    });

    regionMap.set(
      filter.regionSelection?.name!,
      new Region(
        filter.regionSelection?.name!,
        filter.regionSelection?.name!,
        countriesCodes
      )
    );

    return new RegionSelection(regionMap, countriesMap, subnationalMap);
  }

  private getAuthDataFilters(): Partial<DataFilters> {
    return new DataFilters();
  }
}
